import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { SliceEditor } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Checkbox, MedicalImageZoomLayout, Switch } from '@/shared/ui';

import {
  assetsModel,
  isMedicalImagesTitle,
  medicalImageTitles,
} from '@/entities/assets';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { toolbarMIRModel } from '@/entities/toolbarMIR';
import { getDisplayToothNumber, toothModel } from '@/entities/tooth';

import { useAddAndRemoveMedicalImage } from '@/features/medicalImage';
import {
  MedicalImageToolbar,
  useImagePropsSetters,
} from '@/features/medicalImageToolbar';

import styles from './MIRenderModal.module.scss';

export const MIRenderModal: FC = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const {
    visible,
    data: { assetID, toothID },
  } = useAppSelector(modalModel.selectors.selectMIRenderModal);
  const displaySlices = useAppSelector(
    toothModel.selectors.selectDisplaySlicesByToothID(toothID),
  );
  const selectMedicalImage = useMemo(
    () => assetsModel.selectors.makeMedicalImageSelector(assetID),
    [assetID],
  );
  const medicalImage = useAppSelector(selectMedicalImage);
  const tooth = useAppSelector(toothModel.selectors.selectToothByID(toothID));
  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );

  const isSelectionEnabled = true;

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.MIRenderModal));
  }, []);

  const { handleSetAnnotations, handleSetMedicalImageViewOptions } =
    useImagePropsSetters(assetID, toothID);

  const { addMedicalImageToSelected, removeMedicalImageFromSelected } =
    useAddAndRemoveMedicalImage();

  const toggleSelection = () => {
    isTheMedicalImageSelected
      ? removeMedicalImageFromSelected(assetID, toothID)
      : addMedicalImageToSelected(assetID, toothID);
  };

  const isTheMedicalImageSelected = useMemo(
    () => displaySlices.some((item) => item === assetID),
    [displaySlices, assetID],
  );

  const groupTitle = isMedicalImagesTitle(medicalImage?.groupName)
    ? formatMessage(medicalImageTitles[medicalImage.groupName])
    : undefined;

  const index =
    typeof medicalImage?.order === 'number'
      ? medicalImage.order + 1
      : undefined;

  const toothISONumber = tooth?.Numeration?.ISO;

  const toothLabel = toothISONumber
    ? `${formatMessage({ id: 'global.tooth', defaultMessage: 'Tooth' })}${getDisplayToothNumber(toothISONumber, dentalNotationFormat)}`
    : undefined;

  const downloadFileName =
    [toothLabel, groupTitle, index].filter(Boolean).join(' ') || undefined;

  const strideMm = medicalImage?.strideMm;
  const thicknessMm = medicalImage?.thicknessMm;

  const isHideMarks = useSelector(
    toolbarMIRModel.selectors.selectActiveTools('sliceEditor'),
  ).includes('hideMarks');

  const hideMarks = () => {
    dispatch(
      toolbarMIRModel.actions.hideMarks({
        toolbarID: 'sliceEditor',
        value: !isHideMarks,
      }),
    );
  };

  return (
    <MedicalImageZoomLayout
      isOpen={visible}
      order={index}
      strideMm={strideMm}
      thicknessMm={thicknessMm}
      title={groupTitle}
      onClose={handleClose}
      renderToolbar={(toolbarClassName) =>
        !isMobile && (
          <MedicalImageToolbar
            toolbarID="sliceEditor"
            assetID={assetID}
            toothID={toothID}
            hasScale={medicalImage?.hasScale}
            downloadFileName={downloadFileName}
            className={toolbarClassName}
          />
        )
      }
      actions={
        <>
          {isSelectionEnabled && (
            <Checkbox
              checked={isTheMedicalImageSelected ?? false}
              onChange={toggleSelection}
            >
              <FormattedMessage
                id="medicalImages.Selected"
                defaultMessage="Selected"
              />
            </Checkbox>
          )}

          {!!medicalImage?.annotations?.length && (
            <Switch
              checked={isHideMarks}
              onChange={hideMarks}
              className={styles.switch}
            >
              <FormattedMessage
                id="toolName.hideMarks"
                defaultMessage="Hide marks"
              />
            </Switch>
          )}
        </>
      }
    >
      <SliceEditor
        imageID={assetID}
        className={styles.sliceEditor}
        handleSetAnnotations={handleSetAnnotations}
        handleSetMedicalImageViewOptions={handleSetMedicalImageViewOptions}
      />
    </MedicalImageZoomLayout>
  );
};
